import { z } from 'zod';
const color = z.string().regex(/^#[0-9a-fA-F]{6}$/, 'Expected a six-digit hex color');
export const websiteSectionSchema = z.object({
    id: z.string().min(1),
    type: z.enum(['about', 'gallery', 'testimonials', 'cta', 'contact']),
    title: z.string().min(1),
    body: z.string().default(''),
    visible: z.boolean().default(true),
    data: z.record(z.string(), z.unknown()).default({}),
});
export const websiteConfigSchema = z.object({
    theme: z.object({ primary: color, accent: color, background: color, text: color }),
    business: z.object({
        name: z.string().min(1).max(120),
        category: z.string().min(1).max(40).optional(),
        phone: z.string().min(7).max(30),
        email: z.email().optional(),
        location: z.string().min(1).max(160),
    }),
    navigation: z.array(z.object({ label: z.string().min(1), href: z.string().min(1) })).max(10),
    hero: z.object({
        eyebrow: z.string().optional(),
        headline: z.string().min(1).max(140),
        highlightedText: z.string().optional(),
        description: z.string().min(1).max(500),
        primaryCTA: z.string().min(1).max(60),
        secondaryCTA: z.string().max(60).optional(),
        image: z.url().optional(),
    }),
    stats: z.array(z.object({ value: z.string().min(1), label: z.string().min(1) })).max(8),
    proof: z.object({ title: z.string().min(1), rating: z.string().min(1), detail: z.string().min(1) }).optional(),
    services: z.array(z.object({ title: z.string().min(1), description: z.string().min(1), icon: z.string().optional() })).min(1).max(12),
    sections: z.array(websiteSectionSchema),
    assetPolicy: z.object({
        status: z.enum(['proposal-only', 'approved']),
        sourceUrls: z.array(z.url()).max(20),
    }).optional(),
});
export const prospectStatusSchema = z.enum([
    'draft', 'sent', 'viewed', 'changes_requested', 'meeting_scheduled',
    'checkout_started', 'purchased', 'archived',
]);
export const prospectBuildStatusSchema = z.enum(['not_started', 'in_development', 'deployed']);
const normalizedLines = (text) => text.split(/\r?\n|\s*[•|]\s*/).map(line => line.replace(/\s+/g, ' ').trim()).filter(Boolean);
const titleCase = (value) => value.toLowerCase().replace(/\b\w/g, letter => letter.toUpperCase()).replace(/\b(Hvac|Ac)\b/g, value => value.toUpperCase());
const serviceDefaults = (text) => {
    const value = text.toLowerCase();
    if (value.includes('roof'))
        return ['Roof Replacement', 'Roof Repair', 'Storm Damage', 'Roof Inspection'];
    if (value.includes('plumb'))
        return ['Plumbing Repair', 'Drain Cleaning', 'Water Heaters', 'Emergency Plumbing'];
    if (value.includes('hvac') || value.includes('heating') || value.includes('air conditioning'))
        return ['AC Repair', 'Heating Service', 'System Installation', 'Preventive Maintenance'];
    if (value.includes('dental') || value.includes('dentist'))
        return ['Preventive Dentistry', 'Restorative Care', 'Cosmetic Dentistry', 'Emergency Visits'];
    if (value.includes('landscap'))
        return ['Landscape Design', 'Lawn Care', 'Seasonal Cleanup', 'Outdoor Maintenance'];
    return ['Professional Services', 'Project Consultation', 'Ongoing Maintenance', 'Customer Support'];
};
export function createProspectBrief(input) {
    const snapshot = input.snapshot;
    const combined = [snapshot?.text, input.websiteText, input.reviewsText, input.socialText, input.notes].filter(Boolean).join('\n');
    const phone = combined.match(/(?:\+?1[\s.-]?)?\(?\d{3}\)?[\s.-]\d{3}[\s.-]\d{4}/)?.[0] ?? snapshot?.detected.phone ?? 'Phone not found';
    const email = combined.match(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i)?.[0] ?? snapshot?.detected.email ?? '';
    const location = input.location.trim() || snapshot?.detected.location || 'Local service area';
    const candidateServices = [...(snapshot?.headings ?? []), ...normalizedLines(input.websiteText ?? '')]
        .filter(line => line.length >= 4 && line.length <= 48 && /repair|replace|install|service|care|clean|inspect|design|maintenance|consult|remodel|treatment|emergency/i.test(line))
        .map(line => titleCase(line.replace(/[^a-z0-9&' -]/gi, '')))
        .filter((line, index, lines) => line && lines.indexOf(line) === index)
        .slice(0, 4);
    const services = candidateServices.length >= 2 ? candidateServices : serviceDefaults(`${input.businessName} ${combined}`);
    const descriptionCandidate = snapshot?.description || normalizedLines(input.websiteText ?? '')[0] || '';
    const description = descriptionCandidate.length >= 35
        ? descriptionCandidate.slice(0, 500)
        : `${input.businessName || snapshot?.detected.businessName || 'This business'} delivers dependable, straightforward service throughout its ${location.toLowerCase()}.`;
    const headlineSubject = services[0]?.replace(/\b(Repair|Replacement|Service|Services|Care)\b/gi, '').trim() || 'Service';
    return {
        businessName: input.businessName.trim() || snapshot?.detected.businessName || snapshot?.title || 'New Business',
        location,
        phone,
        email,
        headline: `${headlineSubject} Done Right.`,
        highlightedText: 'Built Around You.',
        description,
        about: (input.notes || description).slice(0, 700),
        primaryCTA: 'Get a Free Estimate',
        services,
        primaryColor: '#17845b',
        accentColor: '#58bd8b',
        heroImage: input.heroImage || snapshot?.images[0] || '',
        galleryImages: (snapshot?.images ?? []).filter(image => image !== (input.heroImage || snapshot?.images[0] || '')).slice(0, 12),
    };
}
export function createWebsiteConfigFromBrief(brief) {
    const industryText = `${brief.businessName} ${brief.services.join(' ')}`.toLowerCase();
    const category = industryText.includes('roof') ? 'ROOFING' : industryText.includes('hvac') || industryText.includes('heating') || industryText.includes('ac repair')
        ? 'HVAC' : industryText.includes('dental') ? 'DENTAL' : industryText.includes('plumb') ? 'PLUMBING' : 'SERVICES';
    const hero = {
        eyebrow: `Serving ${brief.location}`,
        headline: brief.headline,
        highlightedText: brief.highlightedText,
        description: brief.description,
        primaryCTA: brief.primaryCTA,
        secondaryCTA: 'Explore Our Work',
        ...(brief.heroImage ? { image: brief.heroImage } : {}),
    };
    return websiteConfigSchema.parse({
        theme: { primary: brief.primaryColor, accent: brief.accentColor, background: '#f8f7f2', text: '#18201d' },
        business: { name: brief.businessName, category, phone: brief.phone, ...(brief.email ? { email: brief.email } : {}), location: brief.location },
        navigation: [
            { label: 'Services', href: '#services' }, { label: 'About', href: '#about' },
            { label: 'Our Work', href: '#work' }, { label: 'Contact', href: '#contact' },
        ],
        hero,
        stats: [
            { value: 'Local', label: 'Service' }, { value: 'Clear', label: 'Communication' },
            { value: 'Quality', label: 'Work' }, { value: 'Friendly', label: 'Support' },
        ],
        services: brief.services.map((title, index) => ({
            title,
            description: `Dependable ${title.toLowerCase()} tailored to your property, priorities, and schedule.`,
            icon: ['home', 'hammer', 'cloud-lightning', 'search'][index % 4],
        })),
        sections: [
            { id: 'about', type: 'about', title: `A better experience with ${brief.businessName}.`, body: brief.about, visible: true, data: {} },
            ...(brief.galleryImages.length ? [{ id: 'work', type: 'gallery', title: 'A closer look.', body: 'Selected imagery gathered from the supplied public sources for this private proposal preview.', visible: true, data: { images: brief.galleryImages } }] : []),
            { id: 'cta', type: 'cta', title: 'Ready to get started?', body: `Talk with ${brief.businessName} about your next project.`, visible: true, data: {} },
        ],
        ...(brief.heroImage || brief.galleryImages.length ? { assetPolicy: { status: 'proposal-only', sourceUrls: uniqueUrls([brief.heroImage, ...brief.galleryImages]) } } : {}),
    });
}
function uniqueUrls(values) {
    return values.filter(Boolean).filter((value, index, items) => items.indexOf(value) === index).slice(0, 20);
}
export const austinEliteRoofingConfig = {
    theme: { primary: '#17845b', accent: '#58bd8b', background: '#f8f7f2', text: '#18201d' },
    business: { name: 'Austin Elite Roofing', category: 'ROOFING', phone: '(512) 555-0142', email: 'hello@austineliteroofing.com', location: 'Austin, TX' },
    navigation: [
        { label: 'Services', href: '#services' }, { label: 'About', href: '#about' },
        { label: 'Our Work', href: '#work' }, { label: 'Contact', href: '#contact' },
    ],
    hero: {
        eyebrow: 'Trusted across Austin since 2010', headline: 'Roofing Done Right.', highlightedText: 'Built to Last.',
        description: 'Austin Elite Roofing provides top-quality roofing services for homes and businesses across Austin, TX.',
        primaryCTA: 'Get Free Estimate', secondaryCTA: 'View Our Work',
        image: 'https://images.unsplash.com/photo-1632759145351-1d592919f522?auto=format&fit=crop&w=1800&q=85',
    },
    stats: [
        { value: '15+', label: 'Years Experience' }, { value: '1,000+', label: 'Projects Completed' },
        { value: '5.0', label: 'Google Rating' }, { value: '100%', label: 'Satisfaction' },
    ],
    proof: { title: 'Top-rated local roofers', rating: '★★★★★', detail: '500+ reviews' },
    services: [
        { title: 'Roof Replacement', description: 'Complete roofing systems built with premium materials and meticulous craftsmanship.', icon: 'home' },
        { title: 'Roof Repair', description: 'Fast, dependable repairs that stop leaks and protect the structure beneath.', icon: 'hammer' },
        { title: 'Storm Damage', description: 'Rapid assessment, insurance guidance, and restoration after severe Texas weather.', icon: 'cloud-lightning' },
        { title: 'Roof Inspection', description: 'Clear, photo-documented inspections with practical recommendations and no pressure.', icon: 'search' },
    ],
    sections: [
        { id: 'about', type: 'about', title: 'Built on trust. Proven on every roof.', body: 'Local crews, honest recommendations, and durable workmanship for Central Texas homes and businesses.', visible: true, data: {} },
        { id: 'cta', type: 'cta', title: 'Ready for a roof built to last?', body: 'Get a clear quote from Austin’s trusted roofing team.', visible: true, data: {} },
    ],
};
const prospectSeed = (id, businessName, slug, status, priceCents, views, config = { ...austinEliteRoofingConfig, business: { ...austinEliteRoofingConfig.business, name: businessName } }) => ({ id, businessName, slug, createdAt: '2026-08-26T14:00:00.000Z', status, buildStatus: 'deployed', priceCents, promotionPriceCents: null, schedulingUrl: null, views, lastViewedAt: views ? '2026-08-26T15:30:00.000Z' : null, appointmentAt: null, repositoryUrl: null, contactName: null, contactEmail: null, contactPhone: null, sourceUrls: [], notes: null, demoScheduledAt: null, websiteConfig: config });
export const demoProspects = [
    prospectSeed('10000000-0000-4000-8000-000000000001', 'Austin Elite Roofing', 'austin-elite-roofing', 'sent', 149900, 48, austinEliteRoofingConfig),
    prospectSeed('10000000-0000-4000-8000-000000000002', 'Blue Ridge Plumbing', 'blue-ridge-plumbing', 'viewed', 179900, 31),
    prospectSeed('10000000-0000-4000-8000-000000000003', 'North Star Dental', 'north-star-dental', 'meeting_scheduled', 249900, 22),
    prospectSeed('10000000-0000-4000-8000-000000000004', 'Oakview Roofing', 'oakview-roofing', 'draft', 149900, 0),
    prospectSeed('10000000-0000-4000-8000-000000000005', 'Greenfield HVAC', 'greenfield-hvac', 'changes_requested', 189900, 17),
];
//# sourceMappingURL=index.js.map